// Libraries
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// Components
import CustomMassApprovallProgressSnackbar from '../../../v2/general/CustomSnackbars/CustomMassApprovallProgressSnackbar/CustomMassApprovallProgressSnackbar'
import CustomSnackbarMassApprovalOk from '../../../v2/general/CustomSnackbars/CustomSnackbarMassApprovalOk/CustomSnackbarMassApprovalOk'
import CustomSnackbarMassApprovalError from '../../../v2/general/CustomSnackbars/CustomSnackbarMassApprovalError/CustomSnackbarMassApprovalError'

// Types and Interfaces
import { MassApprovalAPIResponse } from '../../../../types/massApproval'

// Utils
import { selectMassApprovalProgress } from '../../../../store/slices/timesheet/massApprovalSlice'

/**
 * Component for displaying feedback related to the mass approval process.
 *
 * This component handles the display of progress, success, and error notifications
 * during the mass approval of timesheets. It also manages the state of these notifications
 * and provides an error modal for detailed error information.
 */
function MassApprovalFeedback() {
	/**
	 * State to manage the visibility of the success notification.
	 */
	const [isSuccessOpen, setIsSuccessOpen] = useState(false)

	/**
	 * State to manage the visibility of the progress notification.
	 */
	const [isProgressOpen, setIsProgressOpen] = useState(false)

	/**
	 * State to manage the visibility of the error notification.
	 */
	const [isErrorOpen, setIsErrorOpen] = useState(false)

	/**
	 * State to track the progress value of the mass approval process.
	 */
	const [progressValue, setProgressValue] = useState(0)

	/**
	 * Selector to access the mass approval progress from the Redux store.
	 */
	const { progress, data } = useSelector(selectMassApprovalProgress)

	/**
	 * Effect to update the progress value and open the progress notification.
	 */
	useEffect(() => {
		if (progress > 0 && progress <= 100) {
			!isProgressOpen && setIsProgressOpen(true)
			setProgressValue(progress)
		}
	}, [progress])

	/**
	 * Effect to handle the display of success and error notifications based on the mass approval data.
	 */
	useEffect(() => {
		if (data) {
			setIsProgressOpen(false)
			if (data?.failedTimesheets?.length === 0 && data?.feedback?.approved !== 0) {
				setIsSuccessOpen(true)
			}
			if (data?.failedTimesheets?.length > 0) {
				setIsErrorOpen(true)
			}
		}
	}, [data])

	return (
		<div style={{ width: '100%', position: 'relative' }}>
			<CustomMassApprovallProgressSnackbar
				progress={progressValue}
				isOpen={progressValue >= 0 && progressValue < 100 && isProgressOpen}
			/>

			<CustomSnackbarMassApprovalOk
				isOpen={isSuccessOpen}
				closeHandler={() => {
					setIsSuccessOpen(false)
					setProgressValue(0)
					setIsProgressOpen(false)
				}}
			/>

			<CustomSnackbarMassApprovalError
				closeHandler={() => {
					setIsErrorOpen(false)
					setProgressValue(0)
					setIsProgressOpen(false)
				}}
				data={data as unknown as MassApprovalAPIResponse}
				isOpen={isErrorOpen}
			/>
		</div>
	)
}

export default MassApprovalFeedback
